/**
 * Support lookup — read-only, metadata-only BY CONSTRUCTION.
 *
 * This screen answers "what happened to this request" and "what is the state of this account".
 * It does not, and cannot, show the letter, the transcript or the audio:
 * `GET /api/references/{id}/content/{tier}` 403s for staff by design — a support lookup carries
 * no break-glass reason, and the content chokepoint only ever allows a read that names one. So
 * there is no "view content" affordance here, not even a disabled one: an affordance implies the
 * thing is browsable with the right permission, and it is not. The only staff path to a
 * transcript is the audited break-glass on the needs-review queue.
 *
 * The searched email is kept in COMPONENT STATE and never in the URL. A `?email=` in the address
 * bar lands in browser history and, on any navigation away, potentially a referrer. The api-types
 * client puts it in a query string on the wire (that is the API's contract, not ours to change),
 * which is why lib/sentry.ts scrubs `email` from every URL before an event leaves the browser.
 */
import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError, fetchRequest, findUserByEmail } from '@/lib/admin-api';
import { formatTimestamp, type AdminRequest, type AdminUser } from '@/lib/wire';
import { PageHeader } from '@/components/AppShell';
import { ErrorText } from '@/components/DataState';
import { LinkedAccountsDisclosure } from '@/components/LinkedAccounts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TextField } from '@/components/ui/field';

export function Lookup() {
  return (
    <>
      <PageHeader title="Lookup">
        Read-only support view. Status, timeline and account metadata &mdash; never the letter, the
        transcript or the audio.
      </PageHeader>
      <div className="grid gap-4 lg:grid-cols-2">
        <UserLookup />
        <RequestLookup />
      </div>
    </>
  );
}

function notFoundMessage(caught: unknown, subject: string): string {
  if (caught instanceof ApiError && caught.status === 404) return `No ${subject} found.`;
  if (caught instanceof ApiError && caught.status === 400) return 'That doesn’t look right.';
  return 'Couldn’t look that up. Try again in a moment.';
}

function UserLookup() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const lookup = useMutation({
    mutationFn: () => findUserByEmail(email.trim()),
    onSuccess: () => setError(null),
    onError: (caught) => setError(notFoundMessage(caught, 'account with that email')),
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    lookup.reset();
    setError(null);
    lookup.mutate();
  }

  return (
    <section className="rounded-lg border border-line bg-surface p-5">
      <h2 className="font-display text-[14px] font-semibold text-ink">Find an account</h2>
      <form onSubmit={onSubmit} className="mt-3 flex flex-col gap-3" noValidate>
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="off"
          required
        />
        <div>
          <Button type="submit" size="sm" disabled={lookup.isPending || email.trim() === ''}>
            {lookup.isPending ? 'Looking…' : 'Look up'}
          </Button>
        </div>
      </form>

      {error && <ErrorText className="mt-3">{error}</ErrorText>}
      {lookup.data && <UserCard user={lookup.data} />}
    </section>
  );
}

function UserCard({ user }: { user: AdminUser }) {
  return (
    <>
      <dl className="mt-4 flex flex-col gap-2 rounded-md border border-line-2 bg-paper-2 p-3.5 text-[12.5px]">
        <Row label="Name" value={user.displayName} />
        <Row label="Email" value={user.email ?? '—'} />
        <Row label="Phone" value={user.phone ?? '—'} />
        <Row label="Status" value={<Badge tone="neutral">{user.status}</Badge>} />
        <Row label="Id" value={<span className="font-mono text-[11px]">{user.id}</span>} />
        <Row label="Free allowance" value={String(user.freeReferenceAllowance)} />
        <Row label="Requests" value={String(user.referenceRequestCount)} />
        <Row label="Referral code" value={user.referralCode ?? '—'} />
        <Row label="Created" value={formatTimestamp(user.createdAt)} />
        {user.deletedAt && <Row label="Deleted" value={formatTimestamp(user.deletedAt)} />}
        {user.anonymizedAt && <Row label="Anonymized" value={formatTimestamp(user.anonymizedAt)} />}
      </dl>
      {/* Correlation metadata (other accounts on this one's IPs/devices) — still metadata-only, and
          an audited read, so it stays behind a deliberate click rather than loading with the card. */}
      <div className="mt-3">
        <LinkedAccountsDisclosure userId={user.id} note="this account" />
      </div>
    </>
  );
}

function RequestLookup() {
  const [id, setId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const lookup = useMutation({
    mutationFn: () => fetchRequest(id.trim()),
    onSuccess: () => setError(null),
    onError: (caught) => setError(notFoundMessage(caught, 'request with that id')),
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    lookup.reset();
    setError(null);
    lookup.mutate();
  }

  return (
    <section className="rounded-lg border border-line bg-surface p-5">
      <h2 className="font-display text-[14px] font-semibold text-ink">Find a request</h2>
      <form onSubmit={onSubmit} className="mt-3 flex flex-col gap-3" noValidate>
        <TextField
          label="Reference request id"
          value={id}
          onChange={(event) => setId(event.target.value)}
          autoComplete="off"
          className="font-mono text-[12.5px]"
          required
        />
        <div>
          <Button type="submit" size="sm" disabled={lookup.isPending || id.trim() === ''}>
            {lookup.isPending ? 'Looking…' : 'Look up'}
          </Button>
        </div>
      </form>

      {error && <ErrorText className="mt-3">{error}</ErrorText>}
      {lookup.data && <RequestCard request={lookup.data} />}
    </section>
  );
}

function RequestCard({ request }: { request: AdminRequest }) {
  return (
    <div className="mt-4 rounded-md border border-line-2 bg-paper-2 p-3.5">
      <dl className="flex flex-col gap-2 text-[12.5px]">
        <Row label="Candidate" value={request.candidateName} />
        <Row label="Referee" value={request.refereeName} />
        <Row label="Referee email" value={request.refereeEmail ?? '—'} />
        <Row label="Status" value={<Badge tone="neutral">{request.status}</Badge>} />
        <Row label="Visibility" value={request.visibility} />
        <Row label="Requested" value={formatTimestamp(request.requestedAt)} />
        {request.sentAt && <Row label="Sent" value={formatTimestamp(request.sentAt)} />}
        {request.respondedAt && (
          <Row label="Responded" value={formatTimestamp(request.respondedAt)} />
        )}
        {request.signedAt && <Row label="Signed" value={formatTimestamp(request.signedAt)} />}
        <Row label="Expires" value={formatTimestamp(request.expiresAt)} />
        {request.declineReason && <Row label="Decline reason" value={request.declineReason} />}
      </dl>

      {request.timeline.length > 0 && (
        <div className="mt-4">
          <h3 className="font-display text-[11px] font-medium uppercase tracking-wide text-ink-quiet">
            Timeline
          </h3>
          <ol className="mt-2 flex flex-col gap-1.5">
            {request.timeline.map((event, index) => (
              <li
                key={`${event.occurredAt}-${index}`}
                className="flex flex-wrap items-baseline gap-x-2 font-body text-[11.5px] text-ink-70"
              >
                <span className="font-mono text-[11px] text-ink-70">
                  {formatTimestamp(event.occurredAt)}
                </span>
                <span>
                  {event.fromStatus ? `${event.fromStatus} → ` : ''}
                  <strong className="font-display font-medium text-ink">{event.toStatus}</strong>
                </span>
                <span className="text-ink-70">by {event.actorType}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <dt className="font-display text-[11px] font-medium uppercase tracking-wide text-ink-quiet">
        {label}
      </dt>
      <dd className="text-right font-body font-medium text-ink">{value}</dd>
    </div>
  );
}
